import { cn } from "@/lib/utils";
import {
  buildMonthGrid,
  buildWeekSegments,
  WEEKDAY_LABELS,
  type JobSpan,
} from "@/lib/calendar-grid";
import { JobChip, type CalendarJob } from "./job-chip";
import { JobSpanBar } from "./job-span-bar";
import { DayOverflowPopover } from "./day-overflow-popover";

const MAX_CHIPS = 3;
const MAX_LANES = 3;

// Kept free of @/hooks/use-api (and therefore of the supabase import) so the
// grid renders in a throwaway harness and unit tests without env vars — the
// container (jobs-calendar.tsx) owns the data fetch.

function toJobSpan(job: CalendarJob): JobSpan {
  return {
    id: job.id,
    start: job.productionStartDate,
    end: job.productionEndDate,
    crew: job.crew,
    status: job.status,
    canceled: job.canceled,
    hasConflict: job.hasCrewConflict ?? false,
  };
}

/**
 * Presentational month grid — a fixed 6×7 Sunday-start grid (decision C1)
 * restructured into 6 per-week rows. Each week row is a CSS Grid with lane
 * rows for spanning bars above the day-cell band. Day chips, +N overflow, and
 * the today ring are unchanged from slice 1. Span bars are placed via explicit
 * gridColumnStart/gridRowStart (never auto-flow, which would displace day
 * cells). ADR-0020 does not apply — span bars are layout primitives.
 */
export function CalendarMonthGrid({
  year,
  month,
  byDate,
  spanningJobs,
  todayKey,
  onOpenJob,
}: {
  year: number;
  month: number;
  /** Jobs bucketed by `YYYY-MM-DD` (see `bucketByDate`). */
  byDate: Map<string, CalendarJob[]>;
  /** Jobs with a valid production window — rendered as spanning bars. */
  spanningJobs?: CalendarJob[];
  /** Local `YYYY-MM-DD` of today, for the accent ring. */
  todayKey: string;
  onOpenJob: (id: string) => void;
}) {
  const grid = buildMonthGrid(year, month);

  const { weeks: weekData } = buildWeekSegments(
    year,
    month,
    (spanningJobs ?? []).map(toJobSpan),
  );
  const weekMap = new Map(weekData.map((w) => [w.weekIndex, w]));

  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)]">
      {/* Weekday header row */}
      <div className="grid grid-cols-7 gap-px bg-[color:var(--color-border)]">
        {WEEKDAY_LABELS.map((d) => (
          <div
            key={d}
            className="bg-[color:var(--color-bg)] px-2 py-1.5 typo-label text-[color:var(--color-text-lo)]"
          >
            {d}
          </div>
        ))}
      </div>

      {/* 6 per-week rows */}
      {Array.from({ length: 6 }, (_, weekIndex) => {
        const weekCells = grid.slice(weekIndex * 7, (weekIndex + 1) * 7);
        const weekInfo = weekMap.get(weekIndex);
        const rawLaneCount = weekInfo?.laneCount ?? 0;
        const laneCount = Math.min(rawLaneCount, MAX_LANES);
        const overflowLanes =
          (weekInfo?.segments ?? []).filter((s) => s.lane >= MAX_LANES).length >
          0;

        return (
          <div
            key={weekIndex}
            className="grid grid-cols-7 gap-px bg-[color:var(--color-border)]"
            style={{
              gridTemplateRows: `repeat(${laneCount + (overflowLanes ? 1 : 0)}, auto) 1fr`,
            }}
          >
            {/* Lane rows — span bars (Layer 2) */}
            {(weekInfo?.segments ?? [])
              .filter((seg) => seg.lane < MAX_LANES)
              .map((seg) => (
                <JobSpanBar
                  key={seg.key}
                  segment={seg}
                  style={{
                    gridColumnStart: seg.startCol + 1,
                    gridColumnEnd: `span ${seg.span}`,
                    gridRowStart: seg.lane + 1,
                  }}
                  onOpenJob={onOpenJob}
                />
              ))}

            {/* "+N spanning" overflow affordance when lanes > MAX_LANES */}
            {overflowLanes && (
              <div
                className="col-span-7 px-1.5 text-xs text-[color:var(--color-text-lo)]"
                style={{ gridRow: MAX_LANES + 1 }}
              >
                +
                {
                  (weekInfo?.segments ?? []).filter((s) => s.lane >= MAX_LANES)
                    .length
                }{" "}
                spanning
              </div>
            )}

            {/* Day-cell band (Layer 1) — unchanged from slice 1 */}
            {weekCells.map((cell, i) => {
              const dayJobs = byDate.get(cell.key) ?? [];
              const isToday = cell.key === todayKey;
              const overflow = dayJobs.length - MAX_CHIPS;
              return (
                <div
                  key={cell.key}
                  style={{
                    gridColumn: i + 1,
                    gridRow: laneCount + (overflowLanes ? 2 : 1),
                  }}
                  className={cn(
                    "min-h-[104px] p-1.5",
                    cell.inMonth
                      ? "bg-[color:var(--color-surface)]"
                      : "bg-[color:var(--color-bg)]",
                  )}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span
                      className={cn(
                        "inline-flex size-6 items-center justify-center rounded-full typo-mono text-xs tabular-nums",
                        isToday &&
                          "ring-1 ring-[color:var(--color-accent)] text-[color:var(--color-accent)]",
                        !isToday &&
                          (cell.inMonth
                            ? "text-[color:var(--color-text-md)]"
                            : "text-[color:var(--color-text-lo)]"),
                      )}
                    >
                      {cell.day}
                    </span>
                  </div>

                  <div className="space-y-0.5">
                    {dayJobs.slice(0, MAX_CHIPS).map((job) => (
                      <JobChip
                        key={job.id}
                        job={job}
                        onOpen={() => onOpenJob(job.id)}
                      />
                    ))}
                    {overflow > 0 && (
                      <DayOverflowPopover
                        jobs={dayJobs.slice(MAX_CHIPS)}
                        onOpenJob={onOpenJob}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
